import { useState, useEffect } from "react";
import { Users, Search, ShieldCheck, KeyRound, Plus, Copy, Trash2, Ticket, Store, RefreshCw, CheckCircle2, Link2, ShoppingBag, Send, MailWarning, Pencil } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, PageSkeleton } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [genCount, setGenCount] = useState(5);
  const [genNote, setGenNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [etsy, setEtsy] = useState(null);
  const [etsyOrders, setEtsyOrders] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [manualEmail, setManualEmail] = useState("");
  const [manualOrderId, setManualOrderId] = useState("");
  const [manualProduct, setManualProduct] = useState("wedding_planner");
  const [orderProductFilter, setOrderProductFilter] = useState("all");
  const [activating, setActivating] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);

  const loadUsers = async (query = "") => {
    try { const { data } = await api.get("/admin/users", { params: { q: query } }); setUsers(data.users); } catch (e) { console.error("loadUsers failed", e); setUsers([]); }
  };
  const loadCodes = async () => {
    try { const { data } = await api.get("/admin/codes"); setCodes(data.codes); } catch (e) { console.error("loadCodes failed", e); setCodes([]); }
  };
  const loadStats = async () => { try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (e) { console.error("loadStats failed", e); } };
  const loadAnalytics = async () => { try { const { data } = await api.get("/admin/demo-analytics"); setAnalytics(data); } catch (e) { console.error("loadAnalytics failed", e); } };
  const loadEtsy = async () => {
    try { const { data } = await api.get("/admin/etsy/status"); setEtsy(data); } catch (e) { console.error("loadEtsy status failed", e); }
    try { const { data } = await api.get("/admin/etsy/orders"); setEtsyOrders(data.orders); } catch (e) { console.error("loadEtsy orders failed", e); }
  };
  const loadOrders = async () => {
    try { const { data } = await api.get("/admin/orders"); setOrders(data.orders); } catch (e) { console.error("loadOrders failed", e); setOrders([]); }
  };
  const manualActivate = async () => {
    if (!manualEmail.includes("@")) { toast.error("Enter a valid buyer email."); return; }
    setActivating(true);
    try {
      const { data } = await api.post("/admin/orders/manual", { buyer_email: manualEmail, etsy_order_id: manualOrderId || null, product_type: manualProduct });
      toast.success(data.linked_existing_user ? "Access granted to existing account" : "Access email sent to buyer");
      setManualEmail(""); setManualOrderId("");
      await Promise.all([loadOrders(), loadStats(), loadUsers(q)]);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't activate."); } finally { setActivating(false); }
  };
  const resendOrder = async (id) => {
    try { await api.post(`/admin/orders/${id}/resend`); toast.success("Access email resent"); loadOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend."); }
  };
  const resendVerification = async (id) => {
    try { await api.post(`/admin/users/${id}/resend-verification`); toast.success("Verification email resent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend."); }
  };
  const openEmailEdit = (type, id, current) => setEmailEdit({ type, id, value: current || "" });
  const saveEmail = async () => {
    const { type, id, value } = emailEdit;
    const email = (value || "").trim();
    if (!email.includes("@")) { toast.error("Enter a valid email."); return; }
    setSavingEmail(true);
    try {
      if (type === "order") {
        const { data } = await api.put(`/admin/orders/${id}/email`, { email });
        toast.success(data.resent ? "Email fixed & access link re-sent" : "Email fixed (couldn't re-send — check email service)");
        await Promise.all([loadOrders(), loadUsers(q)]);
      } else {
        const { data } = await api.put(`/admin/users/${id}/email`, { email });
        toast.success(data.resent ? "Account email updated & notified" : "Account email updated");
        await Promise.all([loadUsers(q), loadOrders()]);
      }
      setEmailEdit(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update email."); }
    finally { setSavingEmail(false); }
  };

  useEffect(() => {
    Promise.all([loadStats(), loadUsers(), loadCodes(), loadEtsy(), loadOrders(), loadAnalytics()]).finally(() => setLoading(false));
    const params = new URLSearchParams(window.location.search);
    const e = params.get("etsy");
    if (e === "connected") toast.success("Etsy shop connected!");
    else if (e === "shopcount") toast.error("We couldn't find a shop on that Etsy account. Connect with the seller account that owns your shop.");
    else if (e && e !== null) toast.error(`Etsy connection error (${e}).`);
  }, []);
  useEffect(() => { const t = setTimeout(() => loadUsers(q), 300); return () => clearTimeout(t); }, [q]);

  const updateUser = async (id, patch) => {
    try {
      await api.put(`/admin/users/${id}`, patch);
      setUsers((p) => p.map((u) => u.id === id ? { ...u, ...patch, entitlement: { ...u.entitlement, ...(patch.plan ? { plan: patch.plan } : {}), ...(patch.status ? { status: patch.status } : {}) } } : u));
      toast.success("Updated");
    } catch { toast.error("Update failed."); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/codes/generate", { count: Number(genCount) || 1, note: genNote });
      toast.success(`${data.generated} code${data.generated > 1 ? "s" : ""} generated`);
      setGenNote("");
      await Promise.all([loadCodes(), loadStats()]);
    } catch { toast.error("Couldn't generate codes."); } finally { setGenerating(false); }
  };

  const deleteCode = async (id) => {
    try { await api.delete(`/admin/codes/${id}`); setCodes((p) => p.filter((c) => c.id !== id)); loadStats(); toast.success("Code deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete."); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Couldn't copy automatically — select the code and copy manually."); }
  };
  const copyUnused = async () => {
    const list = codes.filter((c) => c.status === "unused").map((c) => c.code).join("\n");
    if (!list) { toast.error("No unused codes to copy."); return; }
    try { await navigator.clipboard.writeText(list); toast.success("Copied all unused codes"); }
    catch { toast.error("Couldn't copy automatically. Try copying individual codes."); }
  };

  const connectEtsy = async () => {
    try { const { data } = await api.get("/etsy/connect"); window.location.href = data.authorize_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Etsy isn't configured yet."); }
  };
  const syncEtsy = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/etsy/sync"); toast.success(`Synced — ${data.new_orders} new order${data.new_orders === 1 ? "" : "s"}`); await Promise.all([loadEtsy(), loadCodes(), loadStats()]); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed."); } finally { setSyncing(false); }
  };
  const disconnectEtsy = async () => {
    try { await api.post("/admin/etsy/disconnect"); toast.success("Etsy disconnected"); loadEtsy(); } catch { toast.error("Couldn't disconnect."); }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Manage customers, access codes, Etsy orders and entitlements." testId="admin-header" />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <StatCard index={0} label="Total users" value={stats?.total_users ?? 0} icon={Users} testId="admin-total" />
        <StatCard index={1} label="Active planners" value={stats?.activated_planners ?? 0} testId="admin-planners" />
        <StatCard index={2} label="Lifetime access" value={stats?.premium_users ?? 0} accent icon={ShieldCheck} testId="admin-premium" />
        <StatCard index={3} label="Pending verify" value={stats?.pending_verification ?? 0} icon={MailWarning} testId="admin-pending" />
        <StatCard index={4} label="Orders" value={stats?.total_orders ?? 0} icon={ShoppingBag} testId="admin-orders-count" />
        <StatCard index={5} label="Unredeemed" value={stats?.unredeemed_purchases ?? 0} icon={Ticket} testId="admin-unredeemed" />
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="rounded-full">
          <TabsTrigger value="orders" className="rounded-full" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="codes" className="rounded-full" data-testid="tab-codes">Access Codes</TabsTrigger>
          <TabsTrigger value="etsy" className="rounded-full" data-testid="tab-etsy">Etsy</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="demo" className="rounded-full" data-testid="tab-demo">Demo Analytics</TabsTrigger>
        </TabsList>

        {/* Orders / purchases */}
        <TabsContent value="orders" className="mt-6">
          <div className="rounded-2xl bg-card border border-border p-6 mb-4">
            <div className="flex items-center gap-2 mb-1"><ShoppingBag className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Activate a purchase</h3></div>
            <p className="text-sm text-muted-foreground mb-4">Manually record a purchase and email the buyer a secure access link. Their account links automatically when they sign up (or instantly if they already have one). Use this to fulfil an Etsy order or test the flow.</p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]"><label className="text-xs text-muted-foreground">Buyer email</label><Input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="buyer@example.com" className="rounded-xl mt-1" data-testid="manual-email" /></div>
              <div><label className="text-xs text-muted-foreground">Etsy order # (optional)</label><Input value={manualOrderId} onChange={(e) => setManualOrderId(e.target.value)} placeholder="e.g. 3812345678" className="rounded-xl mt-1 w-44" data-testid="manual-order-id" /></div>
              <div><label className="text-xs text-muted-foreground">Product</label>
                <Select value={manualProduct} onValueChange={setManualProduct}>
                  <SelectTrigger className="rounded-xl mt-1 w-52" data-testid="manual-product"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding_planner">Wedding Planner</SelectItem>
                    <SelectItem value="bachelorette_planner">Bachelorette Planner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={manualActivate} disabled={activating} className="rounded-full" data-testid="manual-activate"><Send className="h-4 w-4 mr-1.5" />{activating ? "Sending…" : "Activate & email link"}</Button>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">No orders yet. Etsy orders appear here automatically, or add one above.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs text-muted-foreground mr-1">Filter by product:</span>
                {[["all", "All"], ["wedding_planner", "Wedding"], ["bachelorette_planner", "Bachelorette"]].map(([val, lab]) => (
                  <button key={val} onClick={() => setOrderProductFilter(val)} data-testid={`order-filter-${val}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${orderProductFilter === val ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    {lab}{val !== "all" && <span className="ml-1 opacity-70">({orders.filter((o) => (o.product_type || "wedding_planner") === val).length})</span>}
                  </button>
                ))}
              </div>
              <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left">
                  {["Buyer email", "Product", "Source", "Order #", "Delivery", "Redeemed by", "Purchased", ""].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {orders.filter((o) => orderProductFilter === "all" || (o.product_type || "wedding_planner") === orderProductFilter).map((o) => (
                    <tr key={o.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid="order-row">
                      <td className="px-4 py-3 whitespace-nowrap">{o.buyer_email || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${(o.product_type || "wedding_planner") === "bachelorette_planner" ? "bg-[#F9ECE8] text-[#6B1D2F]" : "bg-secondary text-primary"}`}>{(o.product_type || "wedding_planner") === "bachelorette_planner" ? "Bachelorette" : "Wedding"}</span></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full ${o.source === "etsy" ? "bg-accent/20 text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>{o.source === "etsy" ? "Etsy" : "Manual"}</span></td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{o.etsy_order_id || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full ${o.delivery_status === "sent" ? "bg-accent/20 text-accent-foreground" : o.delivery_status === "failed" ? "bg-destructive/15 text-destructive" : "bg-secondary text-muted-foreground"}`}>{o.delivery_status === "sent" ? "Emailed" : o.delivery_status === "failed" ? "Failed" : "Pending"}</span></td>
                      <td className="px-4 py-3">{o.status === "redeemed" ? (o.linked_email || "Yes") : <span className="text-muted-foreground">Awaiting</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDate(o.purchase_date || o.created_at)}</td>
                      <td className="px-4 py-3 text-right"><div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => openEmailEdit("order", o.id, o.buyer_email)} data-testid="edit-order-email"><Pencil className="h-3.5 w-3.5 mr-1" />Fix email</Button>
                        {o.buyer_email && <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => resendOrder(o.id)} data-testid="resend-order"><Send className="h-3.5 w-3.5 mr-1" />Resend</Button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* Access codes */}
        <TabsContent value="codes" className="mt-6">
          <div className="rounded-2xl bg-card border border-border p-6 mb-4">
            <div className="flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Generate access codes</h3></div>
            <p className="text-sm text-muted-foreground mb-4">Create one-time lifetime codes, then deliver them to buyers through your Etsy listing.</p>
            <div className="flex flex-wrap items-end gap-3">
              <div><label className="text-xs text-muted-foreground">How many</label><Input type="number" min="1" max="500" value={genCount} onChange={(e) => setGenCount(e.target.value)} className="rounded-xl mt-1 w-28" data-testid="gen-count" /></div>
              <div className="flex-1 min-w-[180px]"><label className="text-xs text-muted-foreground">Note (optional)</label><Input value={genNote} onChange={(e) => setGenNote(e.target.value)} placeholder="e.g. Etsy batch – Sept" className="rounded-xl mt-1" data-testid="gen-note" /></div>
              <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="generate-codes"><Plus className="h-4 w-4 mr-1.5" />{generating ? "Generating…" : "Generate"}</Button>
              <Button variant="outline" onClick={copyUnused} className="rounded-full" data-testid="copy-unused"><Copy className="h-4 w-4 mr-1.5" />Copy all unused</Button>
            </div>
          </div>

          {codes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">No codes yet. Generate your first batch above.</div>
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left">
                  {["Code", "Status", "Source", "Redeemed by", "Created", ""].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid="code-row">
                      <td className="px-4 py-3 font-mono tracking-wider">{c.code}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full ${c.status === "redeemed" ? "bg-secondary text-muted-foreground" : "bg-accent/20 text-accent-foreground"}`}>{c.status === "redeemed" ? "Redeemed" : "Available"}</span></td>
                      <td className="px-4 py-3 text-muted-foreground">{c.source === "etsy" ? `Etsy #${c.receipt_id}` : (c.note || "Manual")}</td>
                      <td className="px-4 py-3">{c.redeemed_email || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3"><div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copy(c.code)} data-testid="copy-code"><Copy className="h-4 w-4" /></Button>
                        {c.status !== "redeemed" && c.source !== "etsy" && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteCode(c.id)} data-testid="delete-code"><Trash2 className="h-4 w-4" /></Button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Etsy */}
        <TabsContent value="etsy" className="mt-6">
          <div className="rounded-2xl bg-card border border-border p-6 mb-4" data-testid="etsy-status">
            <div className="flex items-center gap-2 mb-1"><Store className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Etsy auto-sync</h3></div>
            {!etsy?.enabled ? (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground">Automatic order sync isn't configured yet. Add your Etsy API keys to the backend, then connect your shop here.</p>
                <div className="mt-4 rounded-xl bg-secondary/40 p-4 text-sm">
                  <p className="font-medium text-primary mb-2">To enable:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                    <li>Create an app at developers.etsy.com and copy your <b>keystring</b> + <b>shared secret</b>.</li>
                    <li>Register this redirect URI in your Etsy app: <code className="text-xs bg-background px-1.5 py-0.5 rounded">{etsy?.redirect_uri}</code></li>
                    <li>Send me the keys and I'll add them — then this page will let you connect.</li>
                  </ol>
                </div>
              </div>
            ) : etsy?.connected ? (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-sm text-accent-foreground"><CheckCircle2 className="h-4 w-4 text-accent" /> Connected to <b>{etsy.shop_name || "your shop"}</b></div>
                <p className="text-sm text-muted-foreground mt-1">Last sync: {etsy.last_sync ? formatDate(etsy.last_sync) : "never"} · {etsy.order_count} order{etsy.order_count === 1 ? "" : "s"} imported · Auto-syncs every 15 min.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button onClick={syncEtsy} disabled={syncing} className="rounded-full" data-testid="etsy-sync"><RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync now"}</Button>
                  <Button variant="outline" onClick={disconnectEtsy} className="rounded-full" data-testid="etsy-disconnect">Disconnect</Button>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground">Etsy is configured. Connect your shop to start importing paid orders automatically.</p>
                <Button onClick={connectEtsy} className="rounded-full mt-4" data-testid="etsy-connect"><Link2 className="h-4 w-4 mr-1.5" />Connect Etsy shop</Button>
              </div>
            )}
          </div>

          {etsyOrders.length > 0 && (
            <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left">
                  {["Etsy order", "Buyer email", "Delivery", "Redeemed", "Imported", ""].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {etsyOrders.map((o) => (
                    <tr key={o.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid="etsy-order-row">
                      <td className="px-4 py-3 whitespace-nowrap">#{o.etsy_order_id}</td>
                      <td className="px-4 py-3">{o.buyer_email || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full ${o.delivery_status === "sent" ? "bg-accent/20 text-accent-foreground" : o.delivery_status === "failed" ? "bg-destructive/15 text-destructive" : "bg-secondary text-muted-foreground"}`}>{o.delivery_status === "sent" ? "Emailed" : o.delivery_status === "failed" ? "Failed" : "Pending"}</span></td>
                      <td className="px-4 py-3">{o.status === "redeemed" ? (o.linked_email || "Yes") : "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDate(o.created_at)}</td>
                      <td className="px-4 py-3 text-right">{o.buyer_email && <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => resendOrder(o.id)} data-testid="resend-etsy-order"><Send className="h-3.5 w-3.5 mr-1" />Resend</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <div className="relative max-w-xs mb-4">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="admin-search" />
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/40 text-left">
                {["Email", "Verified", "Plan", "Status", "Signed up", "Access", ""].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid="admin-user-row">
                    <td className="px-4 py-3 whitespace-nowrap">{u.email}{u.role === "admin" && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground">admin</span>}</td>
                    <td className="px-4 py-3">{u.verified ? <span className="text-accent-foreground text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-accent" />Yes</span> : <span className="text-amber-600 text-xs">Pending</span>}</td>
                    <td className="px-4 py-3">
                      <Select value={u.entitlement?.plan || "trial"} onValueChange={(v) => updateUser(u.id, { plan: v })}>
                        <SelectTrigger className="h-8 w-28 rounded-lg text-xs" data-testid="admin-plan"><SelectValue /></SelectTrigger>
                        <SelectContent>{["trial", "lifetime", "premium", "free"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.entitlement?.status || "active"} onValueChange={(v) => updateUser(u.id, { status: v })}>
                        <SelectTrigger className="h-8 w-28 rounded-lg text-xs" data-testid="admin-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{["active", "expired", "cancelled", "trial"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3">{u.disabled ? <span className="text-destructive text-xs">Disabled</span> : <span className="text-accent-foreground text-xs">Enabled</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => openEmailEdit("user", u.id, u.email)} data-testid="edit-user-email"><Pencil className="h-3.5 w-3.5 mr-1" />Edit email</Button>
                        {!u.verified && <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => resendVerification(u.id)} data-testid="admin-resend-verify">Resend code</Button>}
                        <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => updateUser(u.id, { disabled: !u.disabled })} data-testid="admin-toggle-disable">
                          {u.disabled ? "Enable" : "Disable"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Demo analytics */}
        <TabsContent value="demo" className="mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Wedding demo opens", key: "wedding_demo_opened" },
              { label: "Bachelorette demo opens", key: "bachelorette_demo_opened" },
              { label: "Locked actions", key: "demo_locked_action_clicked" },
              { label: "Purchase clicks", key: "demo_purchase_clicked" },
            ].map((s, i) => (
              <div key={s.key} className="rounded-2xl border border-border bg-card p-4" data-testid={`demo-stat-${s.key}`}>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="text-2xl font-serif text-primary mt-1">{analytics?.totals?.[s.key] ?? 0}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{analytics?.last_7d?.[s.key] ?? 0} in last 7 days</p>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/40 text-left">
                {["Product", "Demo opens", "Locked actions", "Purchase clicks"].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {[["wedding_planner", "Wedding Planner"], ["bachelorette_planner", "Bachelorette Planner"]].map(([k, label]) => (
                  <tr key={k} className="border-b border-border last:border-0" data-testid={`demo-product-row-${k}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{label}</td>
                    <td className="px-4 py-3">{analytics?.by_product?.[k]?.opened ?? 0}</td>
                    <td className="px-4 py-3">{analytics?.by_product?.[k]?.locked ?? 0}</td>
                    <td className="px-4 py-3">{analytics?.by_product?.[k]?.purchase_clicked ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!emailEdit} onOpenChange={(o) => !o && setEmailEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl">{emailEdit?.type === "order" ? "Fix buyer email" : "Edit account email"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{emailEdit?.type === "order"
            ? "Update the email on this purchase and re-send the secure access link to the corrected address. If an account already uses the new email, access links to it automatically."
            : "Update this account's email. We'll notify the corrected address with a link to their planner."}</p>
          <div className="mt-3">
            <label className="text-xs text-muted-foreground">Email address</label>
            <Input type="email" value={emailEdit?.value || ""} onChange={(e) => setEmailEdit((p) => ({ ...p, value: e.target.value }))} className="rounded-xl mt-1" data-testid="email-edit-input" autoFocus />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setEmailEdit(null)}>Cancel</Button>
            <Button className="rounded-full" onClick={saveEmail} disabled={savingEmail} data-testid="email-edit-save">{savingEmail ? "Saving…" : "Save & re-send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
